import React, { useCallback, useRef, useState } from 'react';
import { Button, Row, Col } from 'reactstrap';
import Dropzone from 'react-dropzone';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  Intl, LocalTable, ModalForm,
} from 'components';
import bg from 'assets/img/step3Bg.png';
import { connect } from 'react-redux';
import { handlePromise } from 'utils';
import CampaignService from 'api/Campaign';
import SettingsActions from 'store/reducers/Settings';
import GenericContactModal from '../GenericContactModal';

const UploadExcelContactModal = ( {
  campaignId, values, open, onAddContacts, onClose, toggleLoading,
} ) => {
  const modalFormRef = useRef( null );
  const [contacts, setContacts] = useState( _.filter( values.contacts || [], { type: 'excel' } ) );
  const [invalidContactsModalOpened, setInvalidContactsModalOpened] = useState( false );
  const invalidContactsRef = useRef( [] );

  const toggleInvalidContactsModal = useCallback( ( invalidContacts = [] ) => {
    invalidContactsRef.current = invalidContacts;
    setInvalidContactsModalOpened( !invalidContactsModalOpened );
  }, [invalidContactsModalOpened] );

  const uploadFile = useCallback( async ( formData ) => {
    toggleLoading( true );
    const [errors, response, responseData] = await handlePromise(
      CampaignService.uploadPhoneFile( campaignId, {
        originalName: formData.file.name,
        file: formData.file,
        countryCode: values.phoneNumberCountryCode,
      } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return _.isString( errors ) ? 'errors' : 'apiError';
    if ( responseData && responseData.validNumbers && responseData.validNumbers.length ) {
      setContacts( [..._.map( responseData.validNumbers, ( item ) => ( {
        ...item, id: uuidv4(), selected: true, type: 'excel',
      } ) ), ...contacts] );
    }
    if ( responseData && responseData.invalidNumbers && responseData.invalidNumbers.length ) {
      toggleInvalidContactsModal( responseData.invalidNumbers );
    }
  }, [campaignId, contacts, values.phoneNumberCountryCode,
    toggleInvalidContactsModal, toggleLoading] );

  const toggleSelected = useCallback( ( row ) => {
    if ( row.original.id ) {
      const newContacts = _.cloneDeep( contacts );
      const contactIndex = _.findIndex( newContacts, { id: row.original.id } );
      if ( contactIndex !== -1 ) {
        newContacts[contactIndex].selected = !newContacts[contactIndex].selected;
      }
      setContacts( newContacts );
    }
  }, [contacts] );

  return (
    <ModalForm
      title="uploadExcelModalTitle"
      showErrorsInHeader
      size="lg"
      open={open}
      onClose={onClose}
      bodyStyle={{ minHeight: 250 }}
      onSubmit={uploadFile}
      body={( modalForm ) => {
        modalFormRef.current = modalForm;
        return (
          <>
            <Row className="my-3">
              <Col md={6} className="d-flex flex-column justify-content-center" style={{ minHeight: 240 }}>
                <img
                  src={bg}
                  alt="..."
                  className="campaign-wizard-triangle img-fluid d-none d-lg-block"
                  style={{ minHeight: 240 }}
                />

                <Dropzone
                  accept=".csv,.xls,.xlsx,.txt"
                  onDrop={( files ) => {
                    if ( files && files[0] ) {
                      modalForm.change( 'file', files[0] );
                      modalForm.submit();
                    }
                  }}
                >
                  {( { getRootProps, getInputProps } ) => (
                    <Row noGutters className="w-100">
                      <Col md={9} xs={12} className="m-auto" onClick={getRootProps().onClick}>
                        <input {...getInputProps()} />
                        <Button
                          color="app-default"
                          size="app-sm"
                          type="button"
                          block
                        >
                          <Intl id="selectFile" />
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Dropzone>
              </Col>

              <Col md={5} className="mt-3 mt-md-0 p-md-0 pl-lg-3 d-flex align-items-md-center campaign-wizard-table">
                <LocalTable
                  data={contacts.length < 5
                    ? contacts.concat( new Array( 5 - contacts.length ).fill( { empty: true } ) )
                    : contacts}
                  manualPagination
                  onlyTable
                  getRowProps={( row ) => ( {
                    onClick: () => toggleSelected( row ),
                    className: row.original.selected ? 'bg-orange-6' : '',
                  } )}
                  columns={[
                    {
                      Header: 'nameOrId',
                      accessor: 'nameId',
                      customWidth: 110,
                    },
                    {
                      Header: <Intl
                        id="phonesCount"
                        values={{
                          selected: _.filter( contacts, { selected: true } ).length,
                          total: contacts.length,
                        }}
                      />,
                      disableTranslate: true,
                      accessor: 'phoneNumber',
                      customWidth: 185,
                    },
                  ]}
                />
              </Col>
            </Row>

            {invalidContactsModalOpened
            && (
              <GenericContactModal
                contacts={invalidContactsRef.current}
                modalTitle="wrongPhones"
                tableTitle="wrongPhones"
                open={invalidContactsModalOpened}
                onClose={toggleInvalidContactsModal}
                type="error"
              />
            )}
          </>
        );
      }}
      footer={(
        <Button
          color="app-default"
          size="app-sm"
          className="font-weight-700"
          type="button"
          onClick={() => onAddContacts( contacts, 'excel' )}
        >
          <Intl id="okBtn" />
        </Button>
      )}
    />
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
} );

export default connect( null, mapDispatchToProps )( UploadExcelContactModal );
